<template>
    <div id="vue-repost" v-click-outside="panelClickOutside">
        <div class="u-width-full u-cursor-pointer" v-cloak>
            <div v-if="!standalone" class="ugc__reposted">
                <div class="ugc__action last u-width-full">
                    <div class="ugc__action-community u-cursor-pointer" @click.prevent="panelOpen">
                        <div v-if="repostedItems.length > 0" class="ugc__repost-selected">
                            <img
                                :src="$simpleStore.transformImageWithCloudfront(44, 44, repostedItems[0].image, $root)"
                                :alt="repostedItems[0].name"
                                width="44"
                                height="44"
                                class="ugc__repost-community-icon"
                            >
                            <svg class="ugc__repost-arrows" xmlns="http://www.w3.org/2000/svg" width="18" height="13" viewBox="0 0 22 16" fill="none">
                                <path d="M1 4V6C1 9.31371 3.68629 12 7 12H11M21 12V10C21 6.68629 18.3137 4 15 4H11" stroke="white" stroke-width="2" stroke-linecap="round" />
                                <path d="M14 1L11 4L14 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                <path d="M9 15L12 12L9 9" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </div>
                        <a v-else href="#">
                            <img
                                :src="$cdn('/img2.0/icons/repost.svg')"
                                alt="Repost"
                                width="44"
                                height="44"
                            >
                        </a>
                        <p class="ugc__text">{{ repostedItems.length > 0 ? 'Reposting to ' + repostedItems[0].name : 'Repost to a community' }}</p>
                    </div>
                    <div v-if="repostedItems.length > 0" class="u-cursor-pointer" @click.prevent="clearRepost">
                        <img
                            :src="$cdn('/img/ui/icon-remove-post.svg')"
                            alt="Remove repost"
                            width="14"
                            height="14"
                        >
                    </div>
                </div>
                <input type="hidden" id="getRepostedCommunities" value="" />
            </div>
            <!-- ── Centred modal (post page) ── -->
            <Menu
                v-if="modal"
                :show-menu="open"
                :close-event="modalCloseEvent"
                :top="true"
                :scroll-class="'menu__content--scroll'"
            >
                <div>
                    <div class="menu__title">
                        <p>{{ (standalone && hasReposted) ? 'Your reposts' : 'Repost to a community' }}</p>
                    </div>

                    <!-- Already-reposted view -->
                    <div class="menu__items" v-if="standalone && hasReposted">
                        <div
                            v-for="entry in myReposts"
                            :key="entry.community_id"
                            class="items"
                        >
                            <img
                                v-if="entry.image"
                                :src="$simpleStore.transformImageWithCloudfront(112, 112, 'img/communities/' + entry.community_id + '/' + entry.image, $root)"
                                class="u-radius-4"
                                alt=""
                                width="50"
                                height="50"
                            >
                            <div class="items__details">
                                <p>{{ entry.name }}</p>
                            </div>
                            <button
                                v-if="!confirmRemove || confirmCommunity.community_id !== entry.community_id"
                                class="items__action"
                                @click.stop="confirmRemoveRepost(entry)"
                            >
                                Remove
                            </button>
                        </div>
                        <div v-if="confirmRemove" class="menu__confirm">
                            <p>Remove repost from <strong>{{ confirmCommunity.name }}</strong>?</p>
                            <div class="u-flex u-gap-2 u-m-t2">
                                <button class="c-btn c-btn--blue new small" :disabled="removingRepost" @click="executeRemoveRepost">
                                    {{ removingRepost ? 'Removing…' : 'Yes, remove' }}
                                </button>
                                <button class="c-btn3 new small" :disabled="removingRepost" @click="confirmRemove = false">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Normal new-repost view -->
                    <template v-else>
                        <div v-if="noCommunitiesAvailable" class="menu__items">
                            <p class="u-color-black u-bold">No communities available</p>
                            <p class="u-color-grey2 u-text-small">You need to be a member of at least one other community to repost this article.</p>
                        </div>
                        <template v-else>
                            <div class="menu__search followers">
                                <div class="search">
                                    <span :class="['search__icon', { 'search__icon--loading': loadingCommunities }]" aria-hidden="true">
                                        <svg class="search__icon-glass" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <circle cx="7" cy="7" r="5.5" stroke="currentColor" stroke-width="1.5"/>
                                            <line x1="11.5" y1="11.5" x2="15" y2="15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                                        </svg>
                                        <svg class="search__icon-spinner" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <circle cx="7" cy="7" r="5.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-dasharray="26 9"/>
                                        </svg>
                                    </span>
                                    <input
                                        type="text"
                                        v-model="search"
                                        @keyup="filterReposts"
                                        class="search"
                                        placeholder="Search communities"
                                    >
                                    <span
                                        :class="['search__clear', search.length > 0 ? 'search__active' : '']"
                                        @click.stop="clearSearch"
                                        aria-label="Clear search"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                            <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                                        </svg>
                                    </span>
                                </div>
                            </div>
                            <div class="menu__items">
                                <div
                                    class="items"
                                    v-for="(repost, index) in repostCommunities"
                                    :class="{ 'u-hidden': repost.isHidden }"
                                    @click="communitySelect($event, index)"
                                >
                                    <img
                                        :src="$simpleStore.transformImageWithCloudfront(112, 112, repost.image, $root)"
                                        class="u-radius-4"
                                        alt=""
                                        width="50"
                                        height="50"
                                    >
                                    <div class="items__details">
                                        <p>{{ repost.name }}</p>
                                    </div>
                                    <button class="items__action" :disabled="repost.reposted">
                                        {{ repost.reposted ? 'Reposted' : 'Repost' }}
                                    </button>
                                </div>
                            </div>
                        </template>
                    </template>
                </div>
            </Menu>

            <!-- ── Side panel (cards / UGC authoring) ── -->
            <div v-else class="c-panel c-panel--repost" v-bind:class="{ open: open }">
                <div class="c-panel__head">
                    <div class="c-panel__logo">
                        <img
                            :src="$cdn($root.brand.key === 'dyadey' ? '/img/logo/dyadey-small.svg' : $root.brand.logo_symbol)"
                            :alt="$root.brand.name + ' Logo'"
                            width="27"
                            height="18"
                        >
                        <p class="u-color-black">Repost</p>
                    </div>
                    <div class="c-panel__close" @click.prevent="panelClose">
                        <img
                            :src="$cdn('/img/ui/icon-close-black.svg')"
                            alt="Close Icon"
                            width="25"
                            height="25"
                        >
                    </div>
                </div>
                <div
                    class="c-panel__content c-panel__content--full"
                    @scroll="onCommunitiesScroll"
                >
                    <!-- ── Already-reposted view (standalone, user has reposted) ── -->
                    <template v-if="standalone && hasReposted">
                        <p class="u-color-black u-bold u-p-b2">Already reposted</p>
                        <p class="u-m-b3">You have reposted this article to the following community.</p>
                        <div
                            v-for="entry in myReposts"
                            :key="entry.community_id"
                            class="c-panel__community-wrap"
                        >
                            <div class="u-width-full">
                            <a href="#0" class="c-panel__community u-flex u-flex-justify-between" @click.prevent>
                                <div class="c-panel__community-details u-flex u-flex-align-center">
                                    <div class="c-panel__members">
                                        <img
                                            v-if="entry.image"
                                            :src="$simpleStore.transformImageWithCloudfront(112, 112, 'img/communities/' + entry.community_id + '/' + entry.image, $root)"
                                            class="c-panel__community-icon u-radius-4"
                                            alt=""
                                        >
                                        <div class="c-panel__community-details">
                                            <p class="u-color-black">{{ entry.name }}</p>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    v-if="!confirmRemove || confirmCommunity.community_id !== entry.community_id"
                                    class="c-btn c-btn--blue new small"
                                    @click.stop="confirmRemoveRepost(entry)"
                                >
                                    Remove repost
                                </button>
                            </a>
                            </div>
                            <div v-if="confirmRemove && confirmCommunity.community_id === entry.community_id" class="c-panel__confirm u-m-t2">
                                <p class="u-color-black u-text-small u-m-b2">
                                    Remove this repost from <strong>{{ confirmCommunity.name }}</strong>? This cannot be undone.
                                </p>
                                <div class="u-flex u-gap-2">
                                    <button class="c-btn c-btn--blue new small" :disabled="removingRepost" @click="executeRemoveRepost">
                                        {{ removingRepost ? 'Removing…' : 'Yes, remove' }}
                                    </button>
                                    <button class="c-btn3 new small" :disabled="removingRepost" @click="confirmRemove = false">
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- ── Normal new-repost view ── -->
                    <template v-else>
                        <p class="u-color-black u-bold u-p-b2">
                            Repost to one other community
                        </p>
                        <p class="u-m-b3">Share this article with members of another community you belong to.</p>
                        <div v-if="noCommunitiesAvailable" class="c-panel__community-noresults">
                            <p class="u-color-black u-bold">No communities available</p>
                            <p class="u-color-grey2 u-text-small">
                                You need to be a member of at least one other community to repost this article.
                            </p>
                        </div>
                        <template v-else>
                            <input
                                class="c-textbox c-textbox--grey u-width-full u-m-b3"
                                type="text"
                                name="repost-filter"
                                id="repost-filter"
                                placeholder="Search communities..."
                                v-model="search"
                                @input="filterReposts"
                            />
                            <div
                                class="c-panel__community-wrap"
                                v-for="(repost, index) in repostCommunities"
                                :class="{ 'u-hidden': repost.isHidden }"
                            >
                                <div class="u-width-full" @click.prevent="communitySelect($event, index)">
                                    <a
                                        href="#0"
                                        class="c-panel__community u-flex u-flex-justify-between"
                                        @click.prevent="!standalone && $set(repost, 'reposted', !repost.reposted)"
                                        :class="{ reposted: repost.reposted }"
                                    >
                                        <div class="c-panel__community-details u-flex u-flex-align-center">
                                            <div class="c-panel__members">
                                                <img
                                                    :src="$simpleStore.transformImageWithCloudfront(112, 112, repost.image, $root)"
                                                    class="c-panel__community-icon u-radius-4"
                                                    alt=""
                                                >
                                                <div class="c-panel__community-details">
                                                    <p class="u-color-black">{{ repost.name }}</p>
                                                    <div class="c-panel__members">
                                                        <img :src="$cdn('/img/icons/icon-members.svg')" alt="Members Icon" width="24" height="16">
                                                        <p class="member">Members {{ repost.members_count }}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            v-if="standalone"
                                            class="c-btn new small"
                                            :disabled="repost.reposted"
                                            @click.stop="communitySelect($event, index)"
                                        >
                                            {{ repost.reposted ? 'Reposted' : 'Repost' }}
                                        </button>
                                    </a>
                                </div>
                                <span v-if="repost.joined == null" @click.prevent="$event.target.classList.toggle('fade')" class="c-btn__panel-join">Join</span>
                            </div>
                            <div v-if="showSuggest" class="c-panel__community-noresults">
                                <div>
                                    <p class="u-color-text u-bold">Suggest a community</p>
                                    <p class="u-color-grey2 u-text-small">Can't find the community you are looking for? Why not suggest a community?</p>
                                </div>
                                <div>
                                    <button class="c-panel__community-new" @click.prevent="$simpleStore.suggest($router, 'Create Post')"></button>
                                </div>
                            </div>
                        </template>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Menu from '../menu/Menu';

export default {
    components: { Menu },
    props: {
        standalone:              { type: Boolean, default: false },
        modal:                   { type: Boolean, default: false },
        dyadeyId:                { type: Number,  default: 0 },
        sourceCommunityId:       { type: Number,  default: 0 },
        hasReposted:             { type: Boolean, default: false },
    },
    data() {
        return {
            modalCloseEvent: 'bus-ugc-repost-modal-close',
            open: false,
            loadingCommunities: false,
            search: "",
            repostCommunities: [],
            repostedItems: [],
            communityPage: 1,
            postedItem: null,
            repostId: [],
            showSuggest: false,
            noCommunitiesAvailable: false,
            // Already-reposted state
            myReposts: [],
            confirmRemove: false,
            confirmCommunity: null,
            removingRepost: false,
        };
    },
    created() {
        this.$root.$on(this.modalCloseEvent, this.panelClose);
    },
    methods: {
        clearSearch() {
            setTimeout(() => { this.search = ''; }, 400);
            this.communityPage = 1;
            this.loadingCommunities = true;
            this.$simpleStore
                .getCommunities(this, 1, '', this.$root.user.id)
                .then((communities) => {
                    this.repostCommunities = this.filterCommunities(communities);
                    this.communityPage = 2;
                    this.loadingCommunities = false;
                });
        },
        filterReposts() {
            if (this.timer) {
                clearTimeout(this.timer);
                this.timer = null;
            }
            // get input after 800ms i.e. once user has stopped typing
            this.timer = setTimeout(() => {
                this.communityPage = 1;
                if (this.search.length > 0) {
                    if (this.loadingCommunities) {
                        return;
                    }
                    this.communityPage = 1;
                    this.loadingCommunities = true;
                    this.$simpleStore
                        .getCommunities(
                            this,
                            this.communityPage,
                            this.search,
                            this.$root.user.id
                        )
                        .then((communities) => {
                            this.repostCommunities = this.filterCommunities(communities);
                            this.communityPage++;
                            this.loadingCommunities = false;
                        });
                } else {
                    this.$simpleStore
                        .getCommunities(
                            this,
                            this.communityPage,
                            this.search,
                            this.$root.user.id
                        )
                        .then((communities) => {
                            this.repostCommunities = this.filterCommunities(communities);
                            this.communityPage++;
                            this.loadingCommunities = false;
                        });
                }
            }, 800);
        },
        filterCommunities(communities) {
            return communities.filter(c => {
                if (c.joined == null) return false;
                if (this.sourceCommunityId && parseInt(c.id, 10) === this.sourceCommunityId) return false;
                return true;
            });
        },
        panelOpen() {
            this._justOpened = true;
            this.open = true;
            this.noCommunitiesAvailable = false;
            this.confirmRemove = false;
            this.confirmCommunity = null;

            if (this.standalone && this.hasReposted) {
                // Show the "already reposted" view — fetch which communities this user reposted to
                this.loadingCommunities = true;
                this.$http.get('/api/v1/posts/myreposts/' + this.dyadeyId).then(response => {
                    this.myReposts = response.data.items || [];
                    this.loadingCommunities = false;
                });
            } else {
                // Show the normal community picker
                this.loadingCommunities = true;
                this.$simpleStore
                    .getCommunities(this, 1, this.search, this.$root.user.id)
                    .then((communities) => {
                        this.repostCommunities = this.filterCommunities(communities);
                        this.communityPage++;
                        this.loadingCommunities = false;
                        // Pre-mark already-selected community so it shows as chosen
                        if (!this.standalone && this.repostedItems.length > 0) {
                            const selectedId = this.repostedItems[0].id;
                            this.repostCommunities.forEach(c => {
                                if (c.id === selectedId) this.$set(c, 'reposted', true);
                            });
                        }
                        if (this.standalone && this.repostCommunities.length === 0) {
                            this.noCommunitiesAvailable = true;
                            this.$emit('no-communities');
                        }
                        if (this.modal) {
                            this.$nextTick(() => {
                                const el = this.$el.querySelector('.menu__content--scroll');
                                if (el) el.addEventListener('scroll', this.onCommunitiesScroll);
                            });
                        }
                    });
            }
        },
        panelClose() {
            if (this.modal) {
                const el = this.$el.querySelector('.menu__content--scroll');
                if (el) el.removeEventListener('scroll', this.onCommunitiesScroll);
            }
            this.open = false;
            this.communityPage = 1;
            this.search = "";
            this.confirmRemove = false;
            this.confirmCommunity = null;
            this.myReposts = [];
        },
        panelClickOutside() {
            if (this._justOpened) {
                this._justOpened = false;
                return;
            }
            this.open = false;
            this.communityPage = 1;
            this.search = "";
            this.confirmRemove = false;
            this.confirmCommunity = null;
            this.myReposts = [];
        },
        confirmRemoveRepost(community) {
            this.confirmCommunity = community;
            this.confirmRemove = true;
        },
        executeRemoveRepost() {
            this.removingRepost = true;
            const removed = this.confirmCommunity;
            this.$http.post('/api/v1/posts/removerepost', {
                dyadey_id: this.dyadeyId,
                dest_community_id: removed.community_id,
            }).then(response => {
                this.removingRepost = false;
                this.confirmRemove = false;
                this.confirmCommunity = null;
                // Remove this entry from the local list
                this.myReposts = this.myReposts.filter(e => e.community_id !== removed.community_id);
                // Notify parent of new counts
                this.$emit('repost-removed', {
                    communityCount: response.data.community_count,
                    userHasReposts: response.data.user_has_reposts,
                });
                // Only close the panel once all the user's reposts are removed
                if (!response.data.user_has_reposts) {
                    this.panelClose();
                }
            }, () => {
                this.removingRepost = false;
                this.confirmRemove = false;
            });
        },
        clearRepost() {
            const repostField = document.getElementById("getRepostedCommunities");
            if (repostField) repostField.setAttribute("value", "");
            this.repostedItems = [];
            this.repostId = [];
        },
        communityRemove(id) {
            let repostField = document.getElementById("getRepostedCommunities");
            // loop through reposted items
            for (let i = 0; i < this.repostedItems.length; i++) {
                // check to see if it matches data-id
                if (this.repostedItems[i].id === id) {
                    // remove clicked item from array
                    this.repostedItems.splice(i, 1);
                    // for hidden input field
                    if (this.repostId.indexOf(id) > -1) {
                        // remove items from hidden input if user clicks to remove community
                        this.repostId.splice(this.repostId.indexOf(id), 1);
                        // set the value field on the input with updated data
                        repostField.setAttribute("value", this.repostId);
                    }
                    // break to save resources
                    break;
                }
            }
            // loop through repost items
            for (let i = 0; i < this.repostCommunities.length; i++) {
                // check to see if it matches data-id
                if (this.repostCommunities[i].id === id) {
                    // remove reposted state from slide out panel
                    this.repostCommunities[i].reposted = false;
                    // break to save resources
                    break;
                }
            }
        },
        communitySelect(event, index) {
            const community = this.repostCommunities[index];

            // Standalone mode (article repost): selecting a community immediately reposts and closes
            if (this.standalone) {
                if (community.reposted) {
                    return; // already reposted to this community, nothing to do
                }
                this.$set(community, 'reposted', true);
                this.$http.post('/api/v1/posts/repost', {
                    dyadey_id: this.dyadeyId,
                    community_ids: [parseInt(community.id, 10)]
                }).then(response => {
                    this.panelClose();
                    this.$emit('repost-success', response.data.reposted_to);
                }, () => {
                    this.$set(community, 'reposted', false);
                    this.$emit('repost-error');
                });
                return;
            }

            // UGC creation mode: single community selection
            let repostField = document.getElementById("getRepostedCommunities");

            if (!community.reposted) {
                // User deselected (toggled false by <a> click handler)
                this.repostedItems = [];
                this.repostId = [];
                repostField.setAttribute("value", "");
                return;
            }

            // Deselect any previously chosen community
            if (this.repostedItems.length > 0) {
                const prev = this.repostCommunities.find(c => c.id === this.repostedItems[0].id);
                if (prev) this.$set(prev, 'reposted', false);
            }

            this.repostedItems = [community];
            this.repostId = [JSON.stringify(parseInt(community.id, 10))];
            repostField.setAttribute("value", this.repostId[0]);
            this.panelClose();
        },
        onCommunitiesScroll({
            target: { scrollTop, clientHeight, scrollHeight },
        }) {
            if (this.loadingCommunities) {
                return;
            }
            let percentageScroll = (scrollTop + clientHeight) / scrollHeight;
            // only scroll of scroll height >= 90%
            if (percentageScroll < 0.9) {
                return;
            }
            this.loadingCommunities = true;
            this.$simpleStore
                .getCommunities(
                    this,
                    this.communityPage,
                    this.search,
                    this.$root.user.id
                )
                .then((communities) => {
                    this.repostCommunities.push.apply(
                        this.repostCommunities,
                        this.filterCommunities(communities)
                    );
                    this.communityPage++;
                    this.loadingCommunities = false;
                });
        },
    },
    directives: {
        "click-outside": {
            bind(el, binding, vNode) {
                // Provided expression must evaluate to a function.
                if (typeof binding.value !== "function") {
                    const compName = vNode.context.name;
                    let warn = `[Vue-click-outside:] provided expression '${binding.expression}' is not a function, but has to be`;
                    if (compName) {
                        warn += `Found in component '${compName}'`;
                    }
                    console.warn(warn);
                }
                // Define Handler and cache it on the element
                const bubble = binding.modifiers.bubble;
                const handler = (e) => {
                    if (bubble || (!el.contains(e.target) && el !== e.target)) {
                        binding.value(e);
                    }
                };
                el.__vueClickOutside__ = handler;
                // add Event Listeners
                document.addEventListener("click", handler);
            },
            unbind(el, binding) {
                // Remove Event Listeners
                document.removeEventListener("click", el.__vueClickOutside__);
                el.__vueClickOutside__ = null;
            },
        },
    },
};
</script>
